/* Feb-2026 · Manuscript Cost Pill.
 *
 * Shows what it would cost to finish the manuscript at the current
 * project's selected model — a forward-looking companion to the
 * backward-looking `RunningCostPill`. Numbers update the moment the
 * model changes so writers see the impact of Opus vs GPT vs Gemini
 * before they commit to a draft.
 *
 * Formula:
 *   remaining_words = target_words - current_words
 *   projected_cost   = estimatePriceUsd(remaining_words, active_model)
 *
 * Hidden when there is no project or no target word count on the
 * project (draft/plotting phase — nothing to project).
 */
import React, { useMemo } from 'react';
import { BookOpen } from 'lucide-react';
import { usePW } from '@/lib/store';
import { estimatePriceUsd, fmtUsd } from '@/lib/cost';
import { providerHandle } from '@/lib/providerLabel';
import { cn } from '@/lib/utils';

function modelShapeFor(id) {
  const norm = (id || 'gpt-5-2').toLowerCase();
  if (norm.startsWith('gemini-3-flash')) return { id: norm, provider: 'gemini',    cost_multiplier: 0.35, label: 'Gemini Flash' };
  if (norm.startsWith('gemini-'))        return { id: norm, provider: 'gemini',    cost_multiplier: 2.0,  label: 'Gemini Pro' };
  if (norm.startsWith('gpt-5-4'))        return { id: norm, provider: 'openai',    cost_multiplier: 2.2,  label: 'GPT-5.4' };
  if (norm.startsWith('gpt-'))           return { id: norm, provider: 'openai',    cost_multiplier: 1.1,  label: 'GPT-5.2' };
  if (norm.startsWith('claude-opus'))    return { id: norm, provider: 'anthropic', cost_multiplier: 3.0,  label: 'Claude Opus' };
  if (norm.startsWith('claude-'))        return { id: norm, provider: 'anthropic', cost_multiplier: 1.0,  label: 'Claude Sonnet' };
  return { id: norm, provider: 'openai', cost_multiplier: 1.1, label: 'Default' };
}

export default function ManuscriptCostPill() {
  const { project, chapters } = usePW();

  const { remainingWords, priceUsd, handle, label } = useMemo(() => {
    if (!project) return { remainingWords: 0, priceUsd: 0 };
    const target  = Number(project.target_words) || 0;
    const current = (chapters || []).reduce((s, c) => s + (Number(c.words) || 0), 0);
    const remain  = Math.max(0, target - current);
    const model   = modelShapeFor(project.model_id);
    return {
      remainingWords: remain,
      priceUsd:       remain > 0 ? estimatePriceUsd(remain, model).final_price_usd : 0,
      handle:         providerHandle(model),
      label:          model.label,
    };
  }, [project, chapters]);

  if (!project || remainingWords <= 0) return null;

  const remWordsK = Math.round(remainingWords / 1000);

  return (
    <span
      title={`Finishing this manuscript with ${label} will cost about ${fmtUsd(priceUsd)} (${remWordsK}K words remaining). Switch model in Settings → AI Model.`}
      data-testid="manuscript-cost-pill"
      className={cn(
        'inline-flex h-7 items-center gap-1 rounded-full border border-border bg-secondary/60 px-2 font-mono-stat text-[11px] text-foreground',
      )}
    >
      <BookOpen className="h-3 w-3 text-primary" />
      <span className="hidden md:inline">{handle}</span>
      <span className="opacity-70">·</span>
      {fmtUsd(priceUsd)}
      <span className="hidden opacity-60 md:inline">to finish</span>
    </span>
  );
}
